import localApiAdapter from '../../core/services/localApiAdapter';
import { AGENT_CONTEXT_ENTITY_LIMIT } from '../config/agentConfig';
import {
  buildGenericModuleContext,
  loadGenericModuleData,
  MODULE_BUILDER_CONFIGS,
} from './builders/genericModuleBuilder';

function mapClient(c) {
  return {
    id: c.id,
    name: c.company_name || c.name || c.companyName,
    type: c.client_type || c.type,
    phone: c.phone1 || c.phone,
    email: c.email,
    address: c.address || c.adresse,
    city: c.city || c.ville,
    notes: c.notes || c.note,
  };
}

function mapProduct(p) {
  return {
    id: p.id,
    name: p.name,
    reference: p.reference || p.ref,
    barcode: p.barcode || p.code_barre,
    price: p.price,
    stock: p.stock,
    category: p.category || p.category_name,
    description: p.description,
    notes: p.notes || p.note,
  };
}

function mapSupplier(s) {
  return {
    id: s.id,
    name: s.company_name || s.name || s.companyName,
    phone: s.phone1 || s.phone,
    email: s.email,
    address: s.address || s.adresse,
    city: s.city || s.ville,
    notes: s.notes || s.note,
  };
}

function mapService(s) {
  return {
    id: s.id,
    name: s.name,
    reference: s.reference || s.ref,
    price: s.price,
    category: s.category || s.category_name,
    description: s.description,
    notes: s.notes || s.note,
  };
}

function mapCategory(c) {
  return {
    id: c.id,
    name: c.name,
    description: c.description,
    type: c.type || c.category_type,
  };
}

function mapRelance(r) {
  return {
    id: r.id,
    clientName: r.client_name || r.clientName,
    invoiceNumber: r.invoice_number || r.invoiceNumber,
    amountDue: r.amount_due ?? r.amountDue,
    daysOverdue: r.days_overdue ?? r.daysOverdue,
    status: r.status,
    reminderDate: r.reminder_date || r.reminderDate,
    reminderType: r.reminder_type || r.reminderType,
  };
}

function mapUnpaidDocument(d) {
  const paymentStatus = String(
    d.paymentStatus || d.payment_status || '',
  ).toLowerCase();
  return {
    id: d.id,
    number: d.number,
    clientName: d.clientName || d.client_name,
    amount: d.amount,
    dueDate: d.dueDate || d.due_date,
    paymentStatus,
    status: d.status,
    type: d.type,
  };
}

function isUnpaidDocument(doc) {
  const paymentStatus = String(
    doc.paymentStatus || doc.payment_status || '',
  ).toLowerCase();
  if (['unpaid', 'partial', 'pending', 'overdue'].includes(paymentStatus)) {
    return true;
  }
  const status = String(doc.status || '').toLowerCase();
  return status === 'overdue' || status === 'pending';
}

async function loadRelancesAndUnpaid() {
  const [relances, archives] = await Promise.all([
    localApiAdapter.get('/facturation/relances').catch(() => []),
    localApiAdapter.documents?.getDocuments?.().catch(() => []),
  ]);

  return {
    relances: (Array.isArray(relances) ? relances : []).map(mapRelance),
    unpaidDocuments: (Array.isArray(archives) ? archives : [])
      .filter(isUnpaidDocument)
      .map(mapUnpaidDocument),
  };
}

async function loadViaHttp() {
  const [
    clients,
    products,
    suppliers,
    services,
    categories,
    relanceData,
  ] = await Promise.all([
    localApiAdapter.get('/facturation/clients').catch(() => []),
    localApiAdapter.get('/facturation/products').catch(() => []),
    localApiAdapter.get('/facturation/fournisseurs').catch(() => []),
    localApiAdapter.get('/facturation/services').catch(() => []),
    localApiAdapter.get('/categories').catch(() => []),
    loadRelancesAndUnpaid(),
  ]);

  return {
    clients: (Array.isArray(clients) ? clients : []).map(mapClient),
    products: (Array.isArray(products) ? products : []).map(mapProduct),
    suppliers: (Array.isArray(suppliers) ? suppliers : []).map(mapSupplier),
    services: (Array.isArray(services) ? services : []).map(mapService),
    categories: (Array.isArray(categories) ? categories : []).map(mapCategory),
    relances: relanceData.relances,
    unpaidDocuments: relanceData.unpaidDocuments,
  };
}

async function loadViaElectronIpc() {
  const [
    clients,
    products,
    suppliers,
    services,
    categories,
    relanceData,
  ] = await Promise.all([
    window.electronAPI.get('clients').catch(() => []),
    window.electronAPI.get('products').catch(() => []),
    window.electronAPI.get('suppliers').catch(() => []),
    window.electronAPI.get('services').catch(() => []),
    window.electronAPI.get('categories').catch(() => []),
    loadRelancesAndUnpaid(),
  ]);

  return {
    clients: (Array.isArray(clients) ? clients : []).map(mapClient),
    products: (Array.isArray(products) ? products : []).map(mapProduct),
    suppliers: (Array.isArray(suppliers) ? suppliers : []).map(mapSupplier),
    services: (Array.isArray(services) ? services : []).map(mapService),
    categories: (Array.isArray(categories) ? categories : []).map(mapCategory),
    relances: relanceData.relances,
    unpaidDocuments: relanceData.unpaidDocuments,
  };
}

/**
 * Build local ERP snapshot for cloud AI context (read-only).
 * @param {string} [moduleId] — active agent module for workspace-specific context
 */
export async function buildAgentLocalContext(moduleId) {
  try {
    let data;
    if (window.electronAPI?.get) {
      data = await loadViaElectronIpc();
    } else {
      data = await loadViaHttp();
    }

    const limit = AGENT_CONTEXT_ENTITY_LIMIT;

    let moduleWorkspace = null;
    if (moduleId && moduleId !== 'general' && MODULE_BUILDER_CONFIGS[moduleId]) {
      try {
        const loaded = await loadGenericModuleData(moduleId);
        moduleWorkspace = buildGenericModuleContext(loaded);
      } catch (moduleError) {
        console.warn('AI Agent: module workspace context unavailable', moduleId, moduleError);
      }
    }

    return {
      clients: data.clients.slice(0, limit),
      products: data.products.slice(0, limit),
      suppliers: data.suppliers.slice(0, limit),
      services: (data.services || []).slice(0, limit),
      categories: (data.categories || []).slice(0, limit),
      relances: (data.relances || []).slice(0, limit),
      unpaidDocuments: (data.unpaidDocuments || []).slice(0, limit),
      moduleWorkspace,
      stats: {
        totalClients: data.clients.length,
        totalProducts: data.products.length,
        totalSuppliers: data.suppliers.length,
        totalServices: (data.services || []).length,
        totalCategories: (data.categories || []).length,
        totalRelances: (data.relances || []).length,
        totalUnpaidDocuments: (data.unpaidDocuments || []).length,
      },
    };
  } catch (error) {
    console.error('AI Agent: failed to build local context', error);
    return null;
  }
}

/**
 * Full ERP catalog for Vectorize indexing (no artificial cap).
 */
export async function buildAgentFullCatalog() {
  try {
    let data;
    if (window.electronAPI?.get) {
      data = await loadViaElectronIpc();
    } else {
      data = await loadViaHttp();
    }

    return {
      clients: data.clients,
      products: data.products,
      suppliers: data.suppliers,
      services: data.services || [],
      categories: data.categories || [],
    };
  } catch (error) {
    console.error('AI Agent: failed to build full catalog', error);
    return {
      clients: [],
      products: [],
      suppliers: [],
      services: [],
      categories: [],
    };
  }
}
